import { FC, ReactElement } from "react";
import { useFormContext } from "react-hook-form";

import { InputParameters } from "../types/form-inputs.ts";
import { Button } from "./catalyst/Button.tsx";
import { Dialog } from "./catalyst/Dialog.tsx";
import { DialogActions } from "./catalyst/DialogActions.tsx";
import { DialogBody } from "./catalyst/DialogBody.tsx";
import { DialogDescription } from "./catalyst/DialogDescription.tsx";
import { DialogTitle } from "./catalyst/DialogTitle.tsx";
import { ErrorMessage } from "./catalyst/ErrorMessage.tsx";
import { Field } from "./catalyst/Field.tsx";
import { FieldGroup } from "./catalyst/FieldGroup.tsx";
import { Fieldset } from "./catalyst/Fieldset.tsx";
import { Input } from "./catalyst/Input.tsx";
import { Label } from "./catalyst/Label.tsx";
import { Legend } from "./catalyst/Legend.tsx";

type PlaceholderDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSkip: () => void;
  onSubmit: () => void;
};

const PlaceholderDialog: FC<PlaceholderDialogProps> = ({
  isOpen,
  onClose,
  onSkip,
  onSubmit,
}) => {
  const form = useFormContext();
  const {
    formState: { errors },
    register,
    watch,
  } = form;
  const inputPlaceholders: InputParameters[] = watch("inputPlaceholders") || [];

  const entityPlaceholders = inputPlaceholders.filter(
    (p) => !p.pattern.toLowerCase().startsWith("runtime"),
  );

  const runtimePlaceholders = inputPlaceholders.filter((p) =>
    p.pattern.toLowerCase().startsWith("runtime"),
  );

  const renderFields = (placeholders: InputParameters[]): ReactElement[] =>
    placeholders.map((placeholder) => {
      const label = placeholder.pattern;
      const originalIndex = inputPlaceholders.findIndex(
        (p) => p.pattern === placeholder.pattern,
      );
      const fieldName = `inputPlaceholders.${originalIndex}.value`;
      const errorMessage =
        (
          errors.inputPlaceholders as
            | Record<number, { value?: { message?: string } }>
            | undefined
        )?.[originalIndex]?.value?.message || undefined;

      return (
        <Field key={placeholder.pattern}>
          <Label>{label}</Label>
          <Input
            type="text"
            {...register(fieldName, { required: "This field is required" })}
          />
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </Field>
      );
    });

  return (
    <Dialog onClose={onClose} open={isOpen}>
      <DialogTitle>Enter Placeholder Values</DialogTitle>
      <DialogDescription>
        Please provide values for the detected placeholders.
      </DialogDescription>
      <DialogBody>
        <Fieldset>
          {entityPlaceholders.length > 0 && (
            <FieldGroup>
              <Legend>Entities</Legend>
              {renderFields(entityPlaceholders)}
            </FieldGroup>
          )}
          {runtimePlaceholders.length > 0 && (
            <FieldGroup>
              <Legend>Runtime parameters</Legend>
              {renderFields(runtimePlaceholders)}
            </FieldGroup>
          )}
        </Fieldset>
      </DialogBody>
      <DialogActions
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <Button onClick={onSkip} plain>
          Skip To Manual Entry
        </Button>
        <Button onClick={onClose} plain>
          Cancel
        </Button>
        <Button
          onClick={async () => {
            const isValid = await form.trigger();
            if (isValid) {
              onSubmit();
            }
          }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaceholderDialog;
